import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '@/shared/utils/supabase'
import type { PhotoComment } from '@/shared/api'

interface UsePhotoCommentsRealtimeProps {
  photoId: string
  enabled?: boolean
  onCommentAdded?: (comment: PhotoComment) => void
  onCommentUpdated?: (comment: PhotoComment) => void
  onCommentDeleted?: (commentId: string) => void
}

export function usePhotoCommentsRealtime({
  photoId,
  enabled = true,
  onCommentAdded,
  onCommentUpdated,
  onCommentDeleted
}: UsePhotoCommentsRealtimeProps) {
  const [commentCount, setCommentCount] = useState<number>(0)
  const channelRef = useRef<any>(null)

  const incrementCommentCount = useCallback(() => {
    setCommentCount(prev => prev + 1)
  }, [])

  const decrementCommentCount = useCallback(() => {
    setCommentCount(prev => Math.max(0, prev - 1))
  }, [])

  useEffect(() => {
    if (!enabled || !photoId) {
      return
    }

    // Subscribe to photo_comments changes for this specific photo
    const channel = supabase
      .channel(`photo_comments_${photoId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'photo_comments',
          filter: `photo_id=eq.${photoId}`
        },
        (payload) => {
          incrementCommentCount()
          if (onCommentAdded && payload.new) {
            // Callback will handle adding the comment to the list with proper formatting
            onCommentAdded(payload.new as any)
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'photo_comments',
          filter: `photo_id=eq.${photoId}`
        },
        (payload) => {
          if (onCommentUpdated && payload.new) {
            onCommentUpdated(payload.new as any)
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'photo_comments',
          filter: `photo_id=eq.${photoId}`
        },
        (payload) => {
          decrementCommentCount()
          if (onCommentDeleted && payload.old?.id) {
            onCommentDeleted(payload.old.id)
          }
        }
      )
      .subscribe()

    channelRef.current = channel

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [photoId, enabled, incrementCommentCount, decrementCommentCount, onCommentAdded, onCommentUpdated, onCommentDeleted])

  return {
    commentCount,
    setCommentCount,
    isConnected: channelRef.current?.state === 'SUBSCRIBED'
  }
}
